import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const TimeOffTermination = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Time Off & Termination" 
        description="Schedule flexibility and contract termination policies"
      >
        <section className="space-y-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <p className="text-lg font-medium text-foreground mb-2">
              As an independent contractor, you control your own schedule.
            </p>
            <p className="text-muted-foreground">
              However, reliability and professional communication are essential to maintaining your relationship 
              with BlueLux and serving our customers well.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Schedule Flexibility</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Your Freedom</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-2">
                  <li>Set your own schedule and working hours</li>
                  <li>Accept or decline route assignments through the App</li>
                  <li>Work for other companies or clients</li>
                  <li>Take time off as needed</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium">Your Commitment</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-2">
                  <li>Complete routes you've accepted on schedule</li>
                  <li>Communicate any delays or cancellations immediately through the App</li>
                  <li>Provide advance notice when possible for planned time off</li>
                  <li>Maintain consistent service for customer satisfaction and retention bonuses</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Planned Time Off</h2>
            <p className="text-muted-foreground mb-4">
              When you need to take time off for vacation, personal reasons, or other commitments:
            </p>
            <ol className="list-decimal list-inside space-y-3 text-muted-foreground">
              <li>Notify BlueLux through the App as far in advance as possible</li>
              <li>Coordinate route coverage with BlueLux management</li>
              <li>Communicate with covering technician to ensure smooth handoff</li>
              <li>Provide any special customer notes or considerations</li>
            </ol>
            <div className="bg-muted/50 border border-border rounded-lg p-4 mt-4">
              <p className="text-sm font-medium text-foreground mb-2">Route Coverage Opportunity</p>
              <p className="text-sm text-muted-foreground">
                When you cover another technician's route during their time off, you earn an additional 
                <strong> $5 per pool</strong> for those jobs.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Emergencies & Last-Minute Changes</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">If You Need to Cancel</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-2">
                  <li>Contact BlueLux immediately through the App</li>
                  <li>Provide as much notice as possible</li>
                  <li>Explain the situation (illness, emergency, vehicle breakdown, etc.)</li>
                  <li>Coordinate coverage arrangements</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium">Impact on Bonuses</h3>
                <p className="text-muted-foreground">
                  Missed visits may affect your eligibility for the <strong>$250 quarterly Safety & Reliability 
                  Bonus</strong>, which requires zero missed visits. Plan accordingly and communicate proactively 
                  to maintain your bonus eligibility.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Contract Termination</h2>
            <p className="text-muted-foreground mb-4">
              This independent contractor relationship continues until terminated by either party.
            </p>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-3">Either Party May Terminate</h3>
                <p className="text-muted-foreground mb-3">
                  Either BlueLux or the Contractor may terminate this agreement at any time, with or without cause, 
                  by providing written notice to the other party.
                </p>
                <p className="text-muted-foreground">
                  No specific notice period is required, though professional courtesy is appreciated.
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-3">Reasons BlueLux May Terminate</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Failure to meet Service Standards</li>
                  <li>Repeated customer complaints</li>
                  <li>Incomplete or falsified App documentation</li>
                  <li>Safety violations or policy breaches</li>
                  <li>Breach of confidentiality</li>
                  <li>Violation of company policies</li>
                  <li>Business needs or route restructuring</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Termination Process</h3>
                <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                  <li>Written notice provided to the other party</li>
                  <li>Effective date specified in termination notice</li>
                  <li>Final services completed according to agreement</li>
                  <li>Return of all BlueLux property and materials</li>
                  <li>Final payment processed for completed services</li>
                </ol>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Upon Termination</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Contractor Responsibilities</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-2">
                  <li>Return all BlueLux property, equipment, and materials</li>
                  <li>Delete all customer information and route data from personal devices</li>
                  <li>Return any BlueLux-provided vehicle and keys</li>
                  <li>Settle any outstanding equipment lease or purchase balances</li>
                  <li>Complete any accepted routes or coordinate handoff</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium">BlueLux Responsibilities</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-2">
                  <li>Process final payment for all completed and verified services</li>
                  <li>Pay out any earned bonuses or incentives according to plan terms</li>
                  <li>Provide final payment within 30 days or according to normal payment schedule</li>
                  <li>Issue final Form 1099-NEC at tax time for the year of termination</li>
                </ul>
              </div>

              <div className="bg-card border-2 border-primary rounded-lg p-6">
                <h3 className="text-lg font-semibold text-primary mb-3">Confidentiality Continues</h3>
                <p className="text-muted-foreground">
                  Your confidentiality obligations regarding customer information, business practices, and 
                  proprietary BlueLux information <strong>continue indefinitely</strong> after termination.
                </p>
                <p className="text-muted-foreground mt-3">
                  You may not solicit BlueLux customers, use customer lists, or disclose confidential information 
                  after your contract ends.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-6 mt-8">
            <h3 className="text-lg font-medium mb-3">Leaving on Good Terms</h3>
            <p className="text-sm text-muted-foreground mb-3">
              If you choose to end your contract with BlueLux, providing advance notice and helping coordinate a 
              smooth transition is greatly appreciated. This maintains your professional reputation and keeps doors 
              open for potential future opportunities.
            </p>
            <p className="text-sm text-muted-foreground">
              BlueLux values all contractors and aims to maintain positive relationships even when business 
              arrangements change.
            </p>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default TimeOffTermination;
